import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { StockLocation } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { COUNTRIES } from '@/features/markets';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  emptyLocationForm,
  fromLocation,
  toLocationCreate,
  toLocationUpdate,
} from '../api/mappers';
import { useCreateLocation, useUpdateLocation } from '../hooks/use-location-mutations';
import {
  locationFormSchema,
  type LocationFormInput,
  type LocationFormOutput,
} from '../schemas/location-form-schema';

const NONE = '__none__';

const ADDRESS_FIELDS = ['line1', 'line2', 'city', 'region', 'postalCode'] as const;

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'name',
  ...ADDRESS_FIELDS,
  'countryCode',
  'fulfillsOnlineOrders',
  'isDefault',
]);

/** `body.address.city` lands on `city`. */
const mapField = (field: string) => field.replace(/^address\./, '');

export interface LocationDialogProps {
  storeId: string;
  /** null creates; a location edits it. */
  location: StockLocation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Name, optional address, whether online orders reserve from it, and the default flag. */
export function LocationDialog({ storeId, location, open, onOpenChange }: LocationDialogProps) {
  const { t } = useTranslation(['inventory', 'common']);
  const message = useErrorMessage();
  const create = useCreateLocation(storeId);
  const update = useUpdateLocation(storeId);
  const form = useForm<LocationFormInput, unknown, LocationFormOutput>({
    resolver: useI18nResolver(locationFormSchema),
    defaultValues: location ? fromLocation(location) : emptyLocationForm(),
  });
  const isDefault = useWatch({ control: form.control, name: 'isDefault' });
  const isPending = create.isPending || update.isPending;

  const close = () => {
    onOpenChange(false);
    create.reset();
    update.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      if (location) {
        await update.mutateAsync({ id: location.id, body: toLocationUpdate(values, location) });
      } else {
        const created = await create.mutateAsync(toLocationCreate(values));
        // Create takes no default flag; the new location claims it in a second call.
        if (values.isDefault) {
          await update.mutateAsync({ id: created.id, body: { isDefault: true } });
        }
      }
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {location ? t('inventory:locations.edit.title') : t('inventory:locations.create.title')}
          </DialogTitle>
          <DialogDescription>{t('inventory:locations.create.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('inventory:locations.fields.name')}</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <fieldset className="flex flex-col gap-4 rounded-md border p-3">
              <legend className="px-1 text-sm font-medium">
                {t('inventory:locations.fields.address')}
              </legend>
              <div className="grid gap-4 sm:grid-cols-2">
                {ADDRESS_FIELDS.map((name) => (
                  <FormField
                    key={name}
                    control={form.control}
                    name={name}
                    render={({ field }) => (
                      <FormItem
                        className={
                          name === 'line1' || name === 'line2' ? 'sm:col-span-2' : undefined
                        }
                      >
                        <FormLabel>{t(`inventory:locations.fields.${name}`)}</FormLabel>
                        <FormControl>
                          <Input autoComplete="off" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ))}
                <FormField
                  control={form.control}
                  name="countryCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('inventory:locations.fields.country')}</FormLabel>
                      <Select
                        value={field.value === '' ? NONE : field.value}
                        onValueChange={(value) => field.onChange(value === NONE ? '' : value)}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={NONE}>
                            {t('inventory:locations.fields.noCountry')}
                          </SelectItem>
                          {COUNTRIES.map((country) => (
                            <SelectItem key={country.code} value={country.code}>
                              {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <p className="text-muted-foreground text-xs">
                {t('inventory:locations.fields.addressHint')}
              </p>
            </fieldset>
            <FormField
              control={form.control}
              name="fulfillsOnlineOrders"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between gap-4 rounded-md border p-3">
                  <div className="flex flex-col gap-1">
                    <FormLabel>{t('inventory:locations.fields.fulfillsOnlineOrders')}</FormLabel>
                    <FormDescription>
                      {isDefault
                        ? t('inventory:locations.fields.fulfillsDefault')
                        : t('inventory:locations.fields.fulfillsOnlineOrdersHint')}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={isDefault || field.value}
                      onCheckedChange={field.onChange}
                      disabled={isDefault}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isDefault"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between gap-4 rounded-md border p-3">
                  <div className="flex flex-col gap-1">
                    <FormLabel>{t('inventory:locations.fields.isDefault')}</FormLabel>
                    <FormDescription>
                      {location?.isDefault
                        ? t('inventory:locations.fields.isDefaultLocked')
                        : t('inventory:locations.fields.isDefaultHint')}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={location?.isDefault === true}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? t('common:actions.saving')
                  : location
                    ? t('common:actions.save')
                    : t('inventory:locations.create.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
